import { storageAccount } from '~lib/storage/config';

export type MountTool = 'rclone' | 'blobfuse2';

export const MOUNT_TOOLS: { id: MountTool; label: string }[] = [
  { id: 'rclone', label: 'rclone' },
  { id: 'blobfuse2', label: 'blobfuse2' },
];

// Builds a copy-pasteable shell snippet that mounts a single EPFL folder to a
// local directory. Both tools authenticate with the user's existing `az login`
// rather than an account key, matching how the rest of the app talks to
// storage. `container`/`folder` come straight from the listing, so the snippet
// always targets a real path the signed-in user can already see. Each snippet
// is self-contained: install prerequisites, sign in, then mount read-only.
export function buildMountSnippet(
  tool: MountTool,
  container: string,
  folder: string,
): string {
  if (tool === 'rclone') {
    return buildRcloneSnippet(container, folder);
  }
  return buildBlobfuse2Snippet(container, folder);
}

// Uses rclone's on-the-fly connection syntax (`:azureblob:<container>/<path>`)
// so there's no persisted remote to configure — the backend is set up inline
// via `--azureblob-*` flags. `--azureblob-env-auth` makes rclone pick up the
// Azure CLI credentials from `az login`. The vfs/chunk tuning is what makes
// streaming a multi-GB folder over the network usable.
function buildRcloneSnippet(container: string, folder: string): string {
  return [
    '# 1. Install rclone + macFUSE (the kernel extension it mounts through)',
    'brew install rclone',
    'brew install --cask macfuse',
    '',
    '# 2. Install the Azure CLI and sign in — the mount reuses these credentials',
    '#    Install: https://learn.microsoft.com/cli/azure/install-azure-cli',
    'az login',
    '',
    `# 3. Mount ${container}/${folder} read-only at /mnt/${container}/${folder}`,
    `export MOUNT="/mnt/${container}/${folder}"`,
    `export CACHE="$HOME/.cache/rclone/${folder}"`,
    'sudo mkdir -p "$MOUNT" && sudo chown "$(whoami)" "$MOUNT"',
    'mkdir -p "$CACHE"',
    `rclone mount ":azureblob:${container}/${folder}" "$MOUNT" \\`,
    `  --azureblob-account ${storageAccount} --azureblob-env-auth \\`,
    '  --read-only --vfs-cache-mode full --cache-dir "$CACHE" \\',
    '  --dir-cache-time 1000h \\',
    '  --vfs-read-chunk-size 32M --vfs-read-chunk-size-limit 256M --buffer-size 64M \\',
    `  --volname "${container.toUpperCase()}-${folder}" --log-level INFO`,
    '',
    '# 4. Unmount (run in another terminal, or after Ctrl-C stops the mount)',
    'fusermount -u "$MOUNT" 2>/dev/null || umount "$MOUNT"',
  ].join('\n');
}

// blobfuse2 is Linux-only (it needs FUSE); `--auth-type=azcli` reuses the
// `az login` session, and `--subdirectory` scopes the mount to the one folder
// rather than the whole container.
function buildBlobfuse2Snippet(container: string, folder: string): string {
  return [
    '# 1. Install blobfuse2 (Linux). On Ubuntu/Debian:',
    'sudo apt-get install -y blobfuse2',
    '# Other distros: https://learn.microsoft.com/azure/storage/blobs/blobfuse2-how-to-deploy',
    '',
    '# 2. Install the Azure CLI and sign in — the mount reuses these credentials',
    '#    Install: https://learn.microsoft.com/cli/azure/install-azure-cli',
    'az login',
    '',
    `# 3. Mount ${container}/${folder} read-only at /mnt/${container}/${folder}`,
    `export AZURE_STORAGE_ACCOUNT=${storageAccount}`,
    `export MOUNT="/mnt/${container}/${folder}"`,
    'sudo mkdir -p "$MOUNT" && sudo chown "$(whoami)" "$MOUNT"',
    'blobfuse2 mount "$MOUNT" \\',
    `  --container-name=${container} \\`,
    `  --subdirectory=${folder}/ \\`,
    '  --auth-type=azcli \\',
    '  --read-only=true \\',
    `  --tmp-path=/tmp/blobfuse2-${folder}`,
    '',
    '# 4. Unmount',
    'blobfuse2 unmount "$MOUNT"',
  ].join('\n');
}
